import {
    Positionable,
    SimpleReelModelSymbolPosition,
    SimpleSymbolState
} from "../../core/reel/simplereel/SimpleReelModel";
import {BS2Statics} from "../config/BS2Statics";
import {gsap} from "gsap";
import {BS2GameAssets} from "../BS2GameAssets";
import {BS2} from "../BS2";
import {SpriteUtils} from "../utils/SpriteUtils";
import {BS2Reel} from "./BS2Reel";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";
import {BS2Sound} from "../config/sound/BS2Sound";
import {BS2GameMode} from "../server/BS2GameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";


export enum BS2SymbolStates {
    NORMAL,
    SPIN,
    WIN_HIGHLIGHT,
    NO_WIN,
    EXPLODE,
    WIN_POP,
    REMOVE,
    LANDING,
    ATTENTION,
    UPGRADE,
    UPGRADE_DEAD,
    UPGRADE_REMOVE,
    WIN_REMOVE,
    WIN_DRAIN,
}

export class BS2Symbol extends PIXI.Container implements Positionable {
    public static spineNameMap: { [key: string]: string } = {
        L1: BS2GameAssets.L1_SPINE,
        L2: BS2GameAssets.L2_SPINE,
        L3: BS2GameAssets.L3_SPINE,
        L4: BS2GameAssets.L4_SPINE,
        L5: BS2GameAssets.L5_SPINE,
        M1: BS2GameAssets.M1_SPINE,
        M2: BS2GameAssets.M2_SPINE,
        M3: BS2GameAssets.M3_SPINE,
        M4: BS2GameAssets.M4_SPINE,
        M5: BS2GameAssets.M5_SPINE,
        H1: BS2GameAssets.H1_SPINE,
        H2: BS2GameAssets.H2_SPINE,
        H3: BS2GameAssets.H3_SPINE,
        H4: BS2GameAssets.H4_SPINE,
        H5: BS2GameAssets.H5_SPINE,
        W: BS2GameAssets.W_SPINE,
        S: BS2GameAssets.S_SPINE,
        Y: BS2GameAssets.Y_SPINE,
        D: BS2GameAssets.D_SPINE,
        G: BS2GameAssets.G_SPINE
    };
    protected _symbolName: string;
    protected _mainSprite: PIXI.Sprite;
    protected _view: PIXI.Container;
    protected _spine: PIXI.spine.Spine;
    protected _multiplier: PIXI.BitmapText;
    protected _spineContainer: PIXI.Container;
    protected _symLandingTl: gsap.core.Timeline | undefined;
    private _overlay: PIXI.Sprite;
    private _stateAnimation: gsap.core.Timeline;
    private _state: BS2SymbolStates;
    private _reelsView: BS2Reel;
    private _noWinOverlay: PIXI.Sprite;
    private _overlayFrame: PIXI.Sprite;
    private _symbolPosition?: SimpleReelModelSymbolPosition | undefined;
    private _currMultiValue: number;
    private _maskGfx: PIXI.Graphics;
    private _blackJpegBg: PIXI.Sprite;
    private _verticalSplitLine: PIXI.Sprite;
    private _horizontalSplitLine: PIXI.Sprite;
    private _crossSplitLine: PIXI.Sprite;
    private _isVerticallySplit: boolean = false;
    private _isHorizontallySplit: boolean = false;
    private _isCrossSplit: boolean = false;
    private _numberOfSplitsOnIt: number = 0; //used to keep track how many splits act on this sym. when it becomes 0 means last split takes place and take sym size from server data

    constructor(_symbolName: string, initState: BS2SymbolStates, reelsView: BS2Reel, symbolPosition?: SimpleReelModelSymbolPosition) {
        super();
        if (symbolPosition) {
            this._symbolPosition = symbolPosition;
        }
        this._reelsView = reelsView;
        this._symbolName = _symbolName;
        this._verticalSplitLine = this.createSplitLine("Symbol_split_lines/splitVertical");
        this._horizontalSplitLine = this.createSplitLine("Symbol_split_lines/splitHorizontal");
        this._crossSplitLine = this.createSplitLine("Symbol_split_lines/splitHorizontal&Vertcal");
        this.createOverlay();
        this.createOverlayFrame();
        this.createNoWinOverlay();
        this.createMainSprite(initState);
        this.createMultiplier();
        this.createSpineContainer();
        this.createBlackJpegBg();
        this._view = new PIXI.Container();
        this._view.addChild(this._blackJpegBg);
        this._view.addChild(this._mainSprite);
        this._view.addChild(this._spineContainer);
        this._view.addChild(this._verticalSplitLine, this._horizontalSplitLine, this._crossSplitLine);
        this._view.addChild(this._multiplier);
        this._view.addChild(this._noWinOverlay);
        this._view.addChild(this._overlayFrame);
        this.addChild(this._view);
        this.setMultiplier(1, false);
        this.getStateAnimation(initState);
        this.name = this._symbolName;
    }

    public getSymbolSpine(_symbolName: string): PIXI.spine.Spine | undefined {
        if (this._spine) {
            return this._spine;
        }
        const spinePoolName: string = this.getSpinePoolName(_symbolName);
        if (spinePoolName === undefined) {
            return undefined;
        }
        let spine: PIXI.spine.Spine = BS2.graphicsPool.getSpinePool(spinePoolName).withdraw();
        this._spine = spine;
        return this._spine;
    }

    destroy(options?: { children?: boolean; texture?: boolean; baseTexture?: boolean }): void {
        this._stateAnimation.kill();
        if (this._spine) {
            this._spine.state.clearTracks();
            this._spine.skeleton.setToSetupPose();
            this._spine.skeleton.setBonesToSetupPose();
            this._spine.skeleton.setSlotsToSetupPose();
            this._spine.tint = 0xffffff;
            this._spine.autoUpdate = false;
            this._spine.update(0);
            this._spine.state.setEmptyAnimation(0, 0);
            this._spine.alpha = 1;
            this._spine.visible = true;
            const spinePoolName: string = this.getSpinePoolName(this._symbolName)!;
            BS2.graphicsPool.getSpinePool(spinePoolName).deposit(this._spine);
        }
        super.destroy(options);
    }

    setAvalancheState(state: SimpleSymbolState): void {
        switch (state) {
            case SimpleSymbolState.NORMAL:
                break;
            case SimpleSymbolState.DROP_IN:
                break;
            case SimpleSymbolState.DROP_OUT:
                break;
            case SimpleSymbolState.COLLAPSE_L2R:
                break;
        }
    }

    public moveToWildLocked(object: PIXI.DisplayObject = this): void {
        this._reelsView.layers.wildLocked.addChild(object);
    }

    public moveToMaskedHighLight(object: PIXI.DisplayObject = this): void {
        this._reelsView.layers.maskedHighlight.addChild(object);
    }

    public moveToWinLayer(object: PIXI.DisplayObject = this): void {
        this._reelsView.layers.win.addChild(object);
    }

    public removeFromWinLayer(object: PIXI.DisplayObject = this): void {
        this._reelsView.layers.win.removeChild(object);
    }

    public getNoWinAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._noWinOverlay, {duration: 0.2, alpha: 0.6}), "<");
        return tl;
    }

    public getWinHighlightAnimation(animationName: string = "win_highlight"): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            spine: PIXI.spine.Spine | undefined = this.getSymbolSpine(this._symbolName);
        if (spine) {
            tl.add(() => {
                this.addSpineToView(spine);
                this.moveToWinLayer();
            })
            tl.add(SpineGSAPWrapper.createTimeline(spine, 0, animationName));
            tl.add(gsap.to(this._mainSprite, {duration: 0.001, alpha: 0}), "<");
        }
        return tl;
    }

    public getWinRemoveAnimation(animationName: string = "win", doDrain: boolean = false, doDestroy: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            spine: PIXI.spine.Spine | undefined = this.getSymbolSpine(this._symbolName);

        if (spine) {
            tl.add(() => {
                this.addSpineToView(spine);
                if (doDrain) {
                    this.moveToWinOverLayer();
                } else {
                    this.moveToWinLayer();
                }
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, animationName, false));
            if (doDrain) {
                tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "drain", false));
            }

            if (doDestroy && BS2Statics.HIGH_SYMBOLS.indexOf(this._symbolName) >= 0) {
                tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "win_destroy", false));
                tl.add(gsap.to(this._multiplier, {duration: 0.05, alpha: 0, ease: "none"}), "<");
            }
            tl.add(gsap.to(this._mainSprite, {duration: 0.001, alpha: 0}), "<+=0.1");
        }
        tl.add(gsap.to(this._view, {duration: 0.02, alpha: 0, ease: "none"}));

        return tl;
    }

    public getUpgradeAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            spine: PIXI.spine.Spine | undefined = this.getSymbolSpine(this._symbolName);
        if (spine) {
            tl.add(() => {
                this.addSpineToView(spine);
                this.moveToWinLayer();
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "transform", false));
            tl.add(() => {
                BS2.soundPlayer.playSound(BS2Sound.SYM_TRANSFORM.next());
            }, "<");
            tl.add(gsap.to(this._mainSprite, {duration: 0.001, alpha: 0}), "<");
        }
        return tl;
    }

    public getLandingAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    this._symLandingTl = undefined;
                }
            }),
            spine: PIXI.spine.Spine | undefined = this.getSymbolSpine(this._symbolName);

        if (spine) {
            tl.add(() => {
                this.addSpineToView(spine);
                this.moveToNormalLayer();
            })
            if (BS2Statics.SPLIT_SYMBOLS.includes(this._symbolName)) {
                spine.state.setEmptyAnimation(0, 0);
                spine.state.clearTracks();
                spine.skeleton.setToSetupPose();
                spine.skeleton.setSlotsToSetupPose();
                spine.skeleton.setBonesToSetupPose();
                tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "land", false));
            } else {
                tl.add(SpineGSAPWrapper.createTimeline(spine, 0, "land"));
            }
            tl.add(gsap.to(this._mainSprite, {duration: 0.05, alpha: 0}), "<");
        }
        this._symLandingTl = tl;
        return tl;
    }

    public resetSpineForAS(): void {
        const spine: PIXI.spine.Spine | undefined = this.getSymbolSpine(this._symbolName);
        if (spine) {
            SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "land", false).progress(1);
            spine.state.clearTracks();
            spine.autoUpdate = true;
            spine.skeleton.setToSetupPose();
            spine.skeleton.setBonesToSetupPose();
            spine.skeleton.setSlotsToSetupPose();
            spine.state.setEmptyAnimation(0, 0);
            this.addSpineToView(spine);
        }

    }

    public getStateAnimation(state: BS2SymbolStates): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            isLockedWild: boolean | undefined = (this.symbolPosition && this.symbolPosition.position === BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA) && (BS2.model.gameData ? BS2.model.clientReels.matrix[this._reelsView.id][BS2Statics.BOT_STICKY_WILD_IDX_WITH_BUFFER] === BS2Statics.SYMBOLS.Y : BS2.model.initData.reels[this._reelsView.id][BS2Statics.BOT_STICKY_WILD_IDX_WITH_BUFFER] === BS2Statics.SYMBOLS.Y),
            isBottomStickyWild: boolean | undefined = (this.symbolPosition && this.symbolPosition.position === BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA) && (BS2.model.gameData ? BS2.model.clientReels.matrix[this._reelsView.id][BS2Statics.BOT_STICKY_WILD_IDX_WITH_BUFFER] === BS2Statics.SYMBOLS.W : BS2.model.initData.reels[this._reelsView.id][BS2Statics.BOT_STICKY_WILD_IDX_WITH_BUFFER] === BS2Statics.SYMBOLS.W);
        this._stateAnimation = tl;
        switch (state) {
            case BS2SymbolStates.SPIN:
                tl.add(() => {
                    this.setState(state, "_spin");
                    this.alpha = 0.7;
                    this._blackJpegBg.visible = false;
                });
                break;
            case BS2SymbolStates.NO_WIN:
                tl.add(() => this.setState(state));
                if ((isLockedWild) || (isBottomStickyWild && !BS2.reelView.bottomStickyWildView.isLocked(this._reelsView.id) && BS2.reelView.bottomStickyWildView.isVisible(this._reelsView.id))) {
                    tl.add(BS2.reelView.bottomStickyWildView.playNoWinAnim(this._reelsView.id));
                }
                tl.add(this.getNoWinAnimation(), "<");
                break;
            case BS2SymbolStates.WIN_HIGHLIGHT:
                tl.add(() => this.setState(state));
                tl.add(this.getWinHighlightAnimation());
                break;
            case BS2SymbolStates.WIN_REMOVE:
                tl.add(() => this.setState(state));
                const animName: gsap.core.Timeline = this.getWinRemoveAnimation("win", false, true).pause();
                tl.add([animName.play(), gsap.to([this._blackJpegBg, this._multiplier, this._verticalSplitLine, this._horizontalSplitLine, this._crossSplitLine], {
                    alpha: 0,
                    duration: animName.totalDuration()
                })]);
                if ((BS2Statics.MID_SYMBOLS.includes(this._symbolName) || BS2Statics.HIGH_SYMBOLS.includes(this._symbolName)) && this._symbolPosition && !(BS2.model.data.mode === BS2GameMode.FREESPIN_SUPER || BS2.model.data.mode === BS2GameMode.AVALANCHE_FREESPIN_SUPER)) {
                    tl.add(BS2.reelView.addTextToProgressionBar.moveToProgressionBar(this._symbolPosition?.reelID, this._symbolPosition?.position, this._symbolName, (animName.totalDuration() / 1.5)), "<")
                }
                break;
            case BS2SymbolStates.WIN_DRAIN:
                tl.add(() => this.setState(state));
                const anim: gsap.core.Timeline = this.getWinRemoveAnimation("win", true, false).pause();
                tl.add([anim.play(), gsap.to([this._multiplier, this._blackJpegBg, this._verticalSplitLine, this._horizontalSplitLine, this._crossSplitLine], {
                    alpha: 0,
                    duration: anim.totalDuration()
                })]);
                if ((BS2Statics.MID_SYMBOLS.includes(this._symbolName) || BS2Statics.HIGH_SYMBOLS.includes(this._symbolName)) && this._symbolPosition && !(BS2.model.data.mode === BS2GameMode.FREESPIN_SUPER || BS2.model.data.mode === BS2GameMode.AVALANCHE_FREESPIN_SUPER)) {
                    tl.add(BS2.reelView.addTextToProgressionBar.moveToProgressionBar(this._symbolPosition?.reelID, this._symbolPosition?.position, this._symbolName, (anim.totalDuration() / 1.5)), "<")
                }
                break;
            case BS2SymbolStates.REMOVE:
                tl.add(() => this.setState(state, "none"));
                tl.add(() => {
                    if (this._spine) {
                        this._spine.visible = false;
                    } else {
                        this._mainSprite.visible = false;
                    }
                    this._blackJpegBg.visible = false;
                });
                tl.add(gsap.to(this, {duration: 0.01}));
                break;
            case BS2SymbolStates.UPGRADE_REMOVE:
                tl.add(() => {
                    this.setState(state, "none");
                    this._blackJpegBg.visible = false;
                });
                tl.add(this.upgradeLow(true));
                break;
            case BS2SymbolStates.UPGRADE_DEAD:
                tl.add(() => {
                    this.setState(state);
                    this._blackJpegBg.visible = false;
                });
                tl.add(this.upgradeLow(false));
                break;
            case BS2SymbolStates.LANDING:
                if (this._symbolName == BS2Statics.SYMBOLS.Y || this._symbolName == BS2Statics.SYMBOLS.EM) {
                    tl.add(() => {
                        this.setState(state);
                        if (this._symbolName !== BS2Statics.SYMBOLS.S) {
                            this._blackJpegBg.visible = true;
                        }
                    });
                } else {
                    tl.add(() => {
                        this.setState(state, "none");
                        if (this._symbolName !== BS2Statics.SYMBOLS.S) {
                            this._blackJpegBg.visible = true;
                        }
                    });
                    tl.add(this.getLandingAnimation());
                }
                break;
            case BS2SymbolStates.UPGRADE:
                tl.add(() => this.setState(state, "none"));
                tl.add(this.getUpgradeAnimation());
                break;
            default:
                tl.add(() => this.setState(state));
                if ((isLockedWild) || (isBottomStickyWild && !BS2.reelView.bottomStickyWildView.isLocked(this._reelsView.id) && BS2.reelView.bottomStickyWildView.isVisible(this._reelsView.id))) {
                    tl.add(BS2.reelView.bottomStickyWildView.playAnimAfterNoWin(this._reelsView.id));
                }
                tl.add(() => this.moveToNormalLayer());
                break;
        }


        if (state != BS2SymbolStates.NO_WIN && state != BS2SymbolStates.SPIN) {
            tl.add(gsap.to(this._noWinOverlay, {duration: 0.05, alpha: 0}), 0);
        }
        if (BS2.apiPlugin.settings.get(APISetting.FAST_SPIN) && state !== BS2SymbolStates.SPIN) {
            tl.timeScale(1.5);
        }


        return this._stateAnimation;
    }

    public removeSpinePreSpin() {
        this.setSpriteTexture("", 0);
        this._mainSprite.visible = true;
        this._mainSprite.alpha = 1;
        this.moveToNormalLayer();

        if (this._spine) {
            this._spine.visible = false;
        }

    }

    public getShakeAnimation(duration: number = 1.715): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this.position, {
            duration: duration,
            x: "-=3",
            ease: BS2Statics.bigWinAnticipationWiggle
        }));
        return tl.reverse();
    }

    public setMultiplier(value: number, setAlphaTrue: boolean = true): void {
        this._currMultiValue = value;
        this._multiplier.text = `${value}x`;
        if (setAlphaTrue && value > 1) {
            this._multiplier.alpha = 1;
        }
    }
    public resetMultiplier(): void {
        this._currMultiValue = 1;
        this._multiplier.text = `1x`;
        this._multiplier.alpha = 0;
    }

    public getMultiplierUpdateAnimation(value: number, splitLineDirection: "vertical" | "horizontal" | "cross" | undefined, isRestore: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            multiplierTL: gsap.core.Timeline = gsap.timeline();
        multiplierTL.add(() => {
            if (!isRestore) {
                --this._numberOfSplitsOnIt;
                if (this._numberOfSplitsOnIt === 0 && this._symbolPosition) {
                    value = BS2.model.data.symbolSizes[this._symbolPosition?.reelID][this._symbolPosition?.position];
                }
            }

            this.setMultiplier(value, false);
        });
        multiplierTL.add(gsap.to(this._multiplier, {duration: 0.1, alpha: 1}));
        multiplierTL.add(gsap.to(this._multiplier.scale, {duration: 0.1, x: 1.1, y: 1.1}), "<");
        multiplierTL.add(gsap.to(this._multiplier.scale, {duration: 0.1, x: 1.0, y: 1.0}));
        if (!BS2Statics.SPLIT_SYMBOLS.includes(this._symbolName) && this._symbolName !== BS2Statics.SYMBOLS.W) {
            multiplierTL.add(() => {
                if (splitLineDirection) {
                    this.showSplitLines(splitLineDirection);
                }
            });
        }
        if (this._symbolName !== BS2Statics.SYMBOLS.W) {
            const spine: PIXI.spine.Spine | undefined = this.getSymbolSpine(this._symbolName);
            if (spine) {
                tl.add(() => {
                    this.addSpineToView(spine);
                })
                if (this._symbolName.includes("H")) {
                    tl.add(SpineGSAPWrapper.createTimeline(spine, 0, "mult_up"));
                }
                tl.add(gsap.to(this._mainSprite, {duration: 0.001, alpha: 0}), "<");
            }
        }
        tl.add(multiplierTL, "<");

        return tl;

    }

    public playPotentialWinAttention(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._overlayFrame, {duration: 0.05, alpha: 1, ease: "none"}));
        tl.add(gsap.to(this._overlayFrame, {duration: 0.6, alpha: 0, ease: "none"}));
        return tl;
    }

    public getWinLineFlash(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._overlay, {duration: 0.05, alpha: 1, ease: "power1.out"}));
        tl.add(gsap.to(this._overlayFrame, {duration: 0.05, alpha: 1, ease: "power1.out"}), "<");
        tl.add(gsap.to(this._overlay, {duration: 0.8, alpha: 0, ease: "power1.out"}));
        tl.add(gsap.to(this._overlayFrame, {duration: 0.8, alpha: 0, ease: "power1.out"}), "<");
        return tl;
    }

    public verifyFinalSymSize(finalSymSize: number): number {
        return this._currMultiValue;
    }

    protected addSpineToView(spine: PIXI.spine.Spine): void {
        this._spineContainer.addChild(spine);
        spine.updateTransform();
    }

    protected setSpriteTexture(textureName: string = "", duration: number = 0.05): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (duration == 0) {
            duration = 0.0001;
        }

        if (textureName === "none") {
            const oldAlpha: number = this._mainSprite.alpha;
            tl.add(gsap.to(this._mainSprite, {duration: duration, alpha: 0}))
            tl.add(() => {
                this._mainSprite.texture = PIXI.Texture.EMPTY;
                this._mainSprite.alpha = oldAlpha;
            });
        } else {
            const texture: PIXI.Texture = this.getTexture(textureName),
                tempSprite: PIXI.Sprite = SpriteUtils.makeWithTexture(texture, this._mainSprite.position, this._mainSprite.anchor, 0, this._mainSprite.tint);

            tl.add(() => {
                if (this._mainSprite && this._mainSprite.parent) {
                    this._mainSprite.parent.addChildAt(tempSprite, this._view.getChildIndex(this._mainSprite) + 1);
                }
            })
            tl.add(gsap.to(tempSprite, {duration: duration, alpha: this._mainSprite.alpha}))
            tl.add(() => {
                if (this._mainSprite && this._mainSprite.parent) {
                    this._mainSprite.texture = texture;
                    this._mainSprite.parent.removeChild(tempSprite);
                }
            });

        }
        return tl;
    }

    protected setState(state: BS2SymbolStates, textureName?: string, duration?: number) {
        this._state = state;
        this.setSpriteTexture(textureName, duration);
    }

    protected moveToNormalLayer(object: PIXI.DisplayObject = this) {
        this._reelsView.layers.maskedNormal.addChild(object);
    }

    protected moveToWinOverLayer(object: PIXI.DisplayObject = this) {
        this._reelsView.layers.winOver.addChild(object);
    }

    protected moveToOverlay(object: PIXI.DisplayObject = this) {
        this._reelsView.layers.overlay.addChild(object);
    }

    protected moveTosplitOverlay(object: PIXI.DisplayObject = this) {
        this._reelsView.layers.splitOverlay.addChild(object);
    }

    private upgradeLow(remove: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            spine: PIXI.spine.Spine | undefined = this.getSymbolSpine(this._symbolName);
        if (spine) {
            tl.add(() => {
                this.addSpineToView(spine);
                this.moveToWinLayer();
            })
            if (remove) {
                tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "upgrade_remove", false));
                let durr: number = spine.skeleton.data.findAnimation("upgrade_remove").duration;
                tl.to([this._multiplier, this._blackJpegBg, this._verticalSplitLine, this._horizontalSplitLine, this._crossSplitLine], {
                    alpha: 0,
                    duration: durr / 2
                }, `<+${durr / 2}`);
                tl.add(() => {
                    BS2.soundPlayer.playSound(BS2Sound.SYM_TRANSFORM.next());
                }, `<-${durr / 2}`);
            } else {
                tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "upgrade_dead", false));
                tl.add(() => {
                    BS2.soundPlayer.playSound(BS2Sound.SYM_TRANSFORM.next());
                }, "<");
                tl.add(() => {
                    this.moveToNormalLayer();
                })
            }
            tl.add(gsap.to(this._mainSprite, {duration: 0.001, alpha: 0}), "<");
        }
        return tl;
    }

    private createSplitLine(textureName: string): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(BS2.assetLoader.getTextures(textureName)[0]);
        sprite.anchor.set(0.5, 0.5);
        sprite.name = textureName;
        sprite.visible = false;
        return sprite;
    }

    private showSplitLines(splitName: "vertical" | "horizontal" | "cross"): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (splitName === "vertical") {
            if (this.isVerticallySplit) {
                return tl;
            }
            this._isVerticallySplit = true;
            tl.add(() => {
                this._verticalSplitLine.visible = true;
            });
            tl.to(this._verticalSplitLine, {alpha: 1, ease: "none", duration: 0.1});
        } else if (splitName === "horizontal") {
            if (this.isHorizontallySplit) {
                return tl;
            }
            this._isHorizontallySplit = true;
            tl.add(() => {
                this._horizontalSplitLine.visible = true;
            });
            tl.to(this._horizontalSplitLine, {alpha: 1, ease: "none", duration: 0.1});
        } else {
            if (this._isCrossSplit) {
                return tl;
            }
            this._isCrossSplit = true;
            tl.add(() => {
                this._crossSplitLine.visible = true;
            });
            tl.to(this._crossSplitLine, {alpha: 1, ease: "none", duration: 0.1});
            tl.add(() => {
                this._verticalSplitLine.visible = false;
                this._horizontalSplitLine.visible = false;
            });
        }
        return tl;
    }

    private createBlackJpegBg(): void {
        this._blackJpegBg = new PIXI.Sprite(BS2.assetLoader.getTextures("black")[0]);
        this._blackJpegBg.anchor.set(0.5, 0.5);
        this._blackJpegBg.visible = this._symbolName !== BS2Statics.SYMBOLS.S;
        this._blackJpegBg.name = "_blackJpegBg";
    }

    private createSpineContainer(): void {
        this._spineContainer = new PIXI.Container();
        this._spineContainer.name = "spineContainer";
    }

    private createMultiplier(): void {
        this._multiplier = new PIXI.BitmapText("", {
            fontName: BS2GameAssets.BITMAP_FONT_MULTIPLIER_NUMBER,
            fontSize: 25,
            letterSpacing: -10,
        });
        this._multiplier.position.set(-65, 0);
        this._multiplier.alpha = 0;
        this._multiplier.name = "_multiplier";
    }

    private createMainSprite(initState: BS2SymbolStates): void {
        let initTextureName: string = "";
        if (initState == BS2SymbolStates.SPIN) {
            initTextureName = "_spin";
        }
        this._mainSprite = new PIXI.Sprite(this.getTexture(initTextureName));
        this._mainSprite.anchor.set(0.5, 0.5);
        this._mainSprite.name = "_mainSprite";
    }

    private createNoWinOverlay(): void {
        this._noWinOverlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._noWinOverlay.tint = 0x000000;
        this._noWinOverlay.alpha = 0;
        this._noWinOverlay.anchor.set(0.5, 0.5);
        this._noWinOverlay.width = BS2Statics.SYMBOL_SPRITE_WIDTH;
        this._noWinOverlay.height = BS2Statics.SYMBOL_SPRITE_HEIGHT;
        this._noWinOverlay.name = "noWinOverlay";
    }

    private createOverlayFrame(): void {
        this._overlayFrame = new PIXI.Sprite(BS2.assetLoader.getTextures(BS2GameAssets.SYMBOL_HIGHLIGHT_OVERLAY_FRAME)[0]);
        this._overlayFrame.blendMode = PIXI.BLEND_MODES.ADD;
        this._overlayFrame.tint = PIXI.utils.string2hex("#a99a7c");
        this._overlayFrame.alpha = 0;
        this._overlayFrame.anchor.set(0.5, 0.5);
        this._overlayFrame.height = BS2Statics.SYMBOL_HEIGHT;
        this._overlayFrame.width = BS2Statics.SYMBOL_WIDTH;
        this._overlayFrame.name = "overlayFrame";
    }

    private createOverlay(): void {
        this._overlay = new PIXI.Sprite(BS2.assetLoader.getTextures(BS2GameAssets.SYMBOL_HIGHLIGHT_OVERLAY)[0]);
        this._overlay.blendMode = PIXI.BLEND_MODES.ADD;
        this._overlay.tint = 0xa99a7c;//BS2Statics.symbolTints[this._symbolName];
        this._overlay.alpha = 0;
        this._overlay.anchor.set(0.5, 0.5);
        this._overlay.name = "overlay";
    }

    private getSpinePoolName(_symbolName: string): string {
        return BS2Symbol.spineNameMap[_symbolName];
    }

    private getTexture(textureName: string): PIXI.Texture {
        if (textureName === "none") {
            return PIXI.Texture.EMPTY;
        }
        return BS2.assetLoader.getTextures(BS2GameAssets.getSymbolStateTexturePath(this._symbolName, textureName))[0];
    }

    get numberOfSplitsOnIt(): number {
        return this._numberOfSplitsOnIt;
    }

    set numberOfSplitsOnIt(value: number) {
        this._numberOfSplitsOnIt = value;
    }

    get isHorizontallySplit(): boolean {
        return this._isHorizontallySplit;
    }

    get isVerticallySplit(): boolean {
        return this._isVerticallySplit;
    }

    get currMultiValue(): number {
        return this._currMultiValue;
    }

    set currMultiValue(value: number) {
        this._currMultiValue = value;
    }

    get symbolPosition(): SimpleReelModelSymbolPosition | undefined {
        return this._symbolPosition;
    }

    set symbolPosition(value: SimpleReelModelSymbolPosition | undefined) {
        this._symbolPosition = value;
    }
}